import { useContext } from 'react';
import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { API } from '../../api/endpoints';

/**
 * The licence switches the desktop's Tables tree reads (frmDietech_Resident_DataGrid_V2.Fill_MiscTRL):
 * PM_Allowed_Modules.Diet_Lite_Allowed / Dietech_Assessment_Only, and whether a facility the user is
 * mapped to uses assessments. Each is null when the API could not read it.
 */
export interface TablesMenuFlags {
  dietLite: boolean | null;
  assessmentOnly: boolean | null;
  hasAssessmentFacility: boolean | null;
}

const tablesMenuFlagsQuery = (facilityId: number) => ({
  queryKey: ['tablesMenuFlags', facilityId] as const,
  queryFn: async () => (await apiClient.get<TablesMenuFlags>(API.tablesMenuFlags(facilityId))).data,
  staleTime: 5 * 60 * 1000,
});

/** /api/TablesMenu for the facility, shared by the Tables menu, Items > Item Linking and the main menu. */
export function useTablesMenuFlags(facilityId: number) {
  return useQuery<TablesMenuFlags>({ ...tablesMenuFlagsQuery(facilityId), enabled: facilityId > 0 });
}

// The main menu reads the licence in places that may render without a QueryClientProvider (tests).
const detachedClient = new QueryClient();

/**
 * PM_Allowed_Modules.Dietech_Assessment_Only, for the main menu (useNavContext). True only when the
 * licence says so: while it loads, when it cannot be read, or with no QueryClientProvider, false, and
 * the menu lists the pages as it did before.
 */
export function useAssessmentOnlyLicence(facilityId: number): boolean {
  const contextClient = useContext(QueryClientContext);
  const { data } = useQuery<TablesMenuFlags>(
    { ...tablesMenuFlagsQuery(facilityId), enabled: !!contextClient && facilityId > 0 },
    contextClient ?? detachedClient,
  );
  return data?.assessmentOnly === true;
}
